/* global $ */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function UserDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // ─── Fetch User ───────────────────────────────────────────────────────────────

    const fetchUser = async () => {
        try {
            setLoading(true);
            console.log("=== FETCH USER DEBUG ===");
            console.log("ID:", id);
            console.log("Token in localStorage:", localStorage.getItem("token"));
            const res = await axiosInstance.get(`/api/users/${id}`);
            console.log("Response:", res.data);
            setUser(res.data.data || res.data);
        } catch (error) {
            console.error("=== FETCH USER ERROR ===");
            console.error("Status:", error.response?.status);
            console.error("Data:", error.response?.data);
            console.error("Message:", error.message);
            // Don't let error bubble up to interceptor for now
            setUser(null);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUser(); }, [id]);

    // ─── Delete ───────────────────────────────────────────────────────────────────

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This user will be permanently deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/users/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', text: 'User deleted.', timer: 1500, showConfirmButton: false });
                navigate('/user');
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to delete user' });
            }
        }
    };

    // ─── Helper ───────────────────────────────────────────────────────────────────

    const InfoRow = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <label className="text-muted small mb-1 d-block">{label}</label>
            <span className="fw-medium">{value || <span className="text-muted">—</span>}</span>
        </div>
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleString();
    };

    // ─── Loading / Not Found ──────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger">User not found.</div>
                </div>
                <Footer />
            </div>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn btn-sm btn-outline-secondary me-3"
                                    onClick={() => navigate('/user')}
                                >
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>

                                {/* Avatar + Name */}
                                <div className="d-flex align-items-center">
                                    {user.profile_picture_url ? (
                                        <img
                                            src={user.profile_picture_url}
                                            alt={user.user_name}
                                            className="rounded-circle me-3"
                                            style={{ width: '44px', height: '44px', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div
                                            className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3 text-white"
                                            style={{ width: '44px', height: '44px', fontSize: '18px', fontWeight: 600 }}
                                        >
                                            {user.user_name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <h5 className="mb-0">{user.user_name}</h5>
                                        <small className="text-muted">{user.user_role?.name || ''}{user.department?.name ? ` · ${user.department.name}` : ''}</small>
                                    </div>
                                </div>

                                <span className={`badge ms-3 ${user.isactive ? 'bg-success' : 'bg-danger'}`}>
                                    {user.isactive ? 'Active' : 'Inactive'}
                                </span>

                                {user.force_password_change && (
                                    <span className="badge bg-warning ms-2">Password Change Required</span>
                                )}
                            </div>

                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => navigate('/add-user', { state: { user, isEdit: true } })}
                                >
                                    <i className="fa fa-edit me-1"></i> Edit User
                                </button>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={handleDelete}
                                >
                                    <i className="fa fa-trash-alt me-1"></i> Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-3">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => navigate('/user')}>
                                    Users
                                </span>
                            </li>
                            <li className="breadcrumb-item active">{user.user_name}</li>
                        </ol>
                    </nav>

                    <div className="card mb-0">
                        <div className="card-body">

                            {/* ── Account Info ── */}
                            <h6 className="text-primary border-bottom pb-2 mb-3">Account Information</h6>
                            <div className="row">
                                <InfoRow label="Username" value={user.user_name} />
                                <InfoRow
                                    label="Email"
                                    value={user.email_id
                                        ? <a href={`mailto:${user.email_id}`} className="text-primary">{user.email_id}</a>
                                        : null
                                    }
                                />
                                <InfoRow label="Phone" value={user.phone_no} />
                                <InfoRow
                                    label="Status"
                                    value={
                                        <span className={`badge ${user.isactive ? 'bg-success' : 'bg-danger'}`}>
                                            {user.isactive ? 'Active' : 'Inactive'}
                                        </span>
                                    }
                                />
                                <InfoRow
                                    label="Force Password Change"
                                    value={
                                        <span className={`badge ${user.force_password_change ? 'bg-warning' : 'bg-secondary'}`}>
                                            {user.force_password_change ? 'Yes' : 'No'}
                                        </span>
                                    }
                                />
                                <InfoRow label="Last Login" value={formatDate(user.last_login_at)} />
                                <InfoRow label="Password Changed At" value={formatDate(user.password_changed_at)} />
                            </div>

                            {/* ── Role & Department ── */}
                            <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Role & Department</h6>
                            <div className="row">
                                <InfoRow label="User Type" value={user.user_type?.name} />
                                <InfoRow label="Role" value={user.user_role?.name} />
                                <InfoRow label="Department" value={user.department?.name} />
                            </div>

                            {/* ── Profile Picture ── */}
                            {user.profile_picture_url && (
                                <>
                                    <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Profile Picture</h6>
                                    <div className="mb-3">
                                        <img
                                            src={user.profile_picture_url}
                                            alt={user.user_name}
                                            className="rounded"
                                            style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                        />
                                    </div>
                                </>
                            )}

                        </div>
                    </div>

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default UserDetails;